import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import apiClient from '@/api/client';
import router from '@/router';
import type { UserRole, PersonaInfo } from '@hyperke/shared';

/**
 * User interface
 */
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  must_change_password?: boolean;
  dtc_dashboard_access?: boolean; // granted to admins + DTC_DASHBOARD_ACCESS_EMAILS allowlist
  admin_user_id?: string; // Present during impersonation
  is_impersonation?: boolean; // Flag to indicate impersonation session
  impersonation_session_id?: string; // Backend session row id (end-impersonation contract)
}

/**
 * Login request
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login response
 */
export interface LoginResponse {
  token: string;
  user: User;
  personas?: PersonaInfo[]; // additive; absent for older API builds
}

/**
 * Auth store using Pinia
 * Manages authentication state and actions
 */
export const useAuthStore = defineStore('auth', () => {
  console.log('[Auth Store] Store INITIALIZING - reading localStorage directly');

  // Initialize from localStorage
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  console.log('[Auth Store] Initial localStorage read:', {
    hasToken: !!storedToken,
    tokenPrefix: storedToken ? storedToken.substring(0, 20) + '...' : 'none',
    hasUser: !!storedUser,
  });

  const user = ref<User | null>(storedUser ? JSON.parse(storedUser) : null);
  const token = ref<string | null>(storedToken);
  const loading = ref(false);

  // Impersonation state
  const originalAdminToken = ref<string | null>(localStorage.getItem('original_admin_token'));
  // Mirror of the token stash: the full admin User object, so restoring after
  // impersonation returns the admin's identity (not just their token).
  const originalAdminUser = ref<User | null>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem('original_admin_user') || 'null');
      } catch {
        return null;
      }
    })()
  );
  const isImpersonating = computed(() => user.value?.is_impersonation === true);

  // Persona switcher state (one login, multiple linked role accounts).
  // Hydrated from localStorage so the list survives the hard reload that
  // follows a persona switch. `personas.length > 1` ⇒ show the switcher.
  const personas = ref<PersonaInfo[]>(
    (() => {
      try {
        return JSON.parse(localStorage.getItem('personas') || '[]');
      } catch {
        return [];
      }
    })()
  );
  const personaBaseToken = ref<string | null>(localStorage.getItem('persona_base_token'));
  const hasMultiplePersonas = computed(() => personas.value.length > 1);
  // Boot-time dedupe: fire the GET /auth/personas refresh at most once per
  // page load (see the hook inside checkAuth()).
  let personasLoadAttempted = false;

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  console.log('[Auth Store] Store initialized:', {
    tokenSet: !!token.value,
    userSet: !!user.value,
    userRole: user.value?.role,
    isAuthenticated: isAuthenticated.value,
  });

  /**
   * Login user
   * 
   * @param email - User email
   * @param password - User password
   * @returns Promise<void>
   */
  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    console.log('[Auth Store] Login attempt:', { email });
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', {
        email,
        password,
      });

      console.log('[Auth Store] Login successful:', {
        hasToken: !!response.data.token,
        tokenLength: response.data.token?.length || 0,
        tokenPrefix: response.data.token?.substring(0, 20) + '...',
        user: response.data.user,
        mustChangePassword: response.data.user.must_change_password,
      });

      // Persist token to localStorage FIRST (before updating store)
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Persona list (additive field — [] for unlinked accounts / older API builds)
      localStorage.setItem('personas', JSON.stringify(response.data.personas ?? []));
      // A fresh login is a fresh base token: drop any stash left by a previous
      // session so switchPersona's "stash once" check works off THIS login.
      localStorage.removeItem('persona_base_token');
      console.log('[Auth Store] Token saved to localStorage');

      // Then update store (this ensures localStorage is the source of truth)
      token.value = response.data.token;
      user.value = response.data.user;
      personas.value = response.data.personas ?? [];
      personaBaseToken.value = null;
      console.log('[Auth Store] Store updated:', {
        tokenSet: !!token.value,
        userSet: !!user.value,
        isAuthenticated: isAuthenticated.value,
        mustChangePassword: user.value?.must_change_password,
      });

      // Check if user must change password (first-time login)
      // Check both the response and the store value
      const mustChange = response.data.user.must_change_password === true || 
                        user.value?.must_change_password === true;
      console.log('[Auth Store] Checking password change requirement:', {
        mustChangePassword: mustChange,
        responseValue: response.data.user.must_change_password,
        storeValue: user.value?.must_change_password,
      });
      
      // Use nextTick to ensure router is ready
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (mustChange) {
        console.log('[Auth Store] User must change password, redirecting to ChangePassword');
        router.push({ name: 'ChangePassword', replace: true }).catch((err) => {
          console.error('[Auth Store] Router push error:', err);
          // Fallback: force navigation
          window.location.href = '/change-password';
        });
      } else {
        // Honor a ?redirect= target set by the auth guard (e.g. deep links to
        // /m). Only same-app paths are allowed — never protocol-relative or
        // absolute URLs, to prevent open-redirect. Default stays /leads.
        const redirectQuery = router.currentRoute.value.query.redirect;
        const redirectTarget =
          typeof redirectQuery === 'string' &&
          redirectQuery.startsWith('/') &&
          !redirectQuery.startsWith('//')
            ? redirectQuery
            : '/leads';

        console.log('[Auth Store] No password change required, redirecting to', redirectTarget);
        // use replace to avoid back button issues
        router.push({ path: redirectTarget, replace: true }).catch((err) => {
          console.error('[Auth Store] Router push error:', err);
          // Fallback: force navigation
          window.location.href = redirectTarget;
        });
      }
    } catch (error) {
      console.error('[Auth Store] Login error:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout user
   * Clears all storage (localStorage, sessionStorage, cookies), invalidates session, redirects to login
   * P0.2 Fix: Comprehensive logout to prevent session leaks
   */
  function logout(): void {
    // PERSISTENT LOG: Write to sessionStorage BEFORE clearing it
    // This survives the page clear so we can debug
    const logoutInfo = {
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 8).map(line => line.trim()),
      storeStateBefore: {
        hasToken: !!token.value,
        hasUser: !!user.value,
        userRole: user.value?.role,
        userEmail: user.value?.email,
      },
      localStorageStateBefore: {
        hasToken: !!localStorage.getItem('token'),
        hasUser: !!localStorage.getItem('user'),
        tokenPrefix: localStorage.getItem('token')?.substring(0, 20) + '...',
        userPreview: localStorage.getItem('user')?.substring(0, 100) + '...',
      },
      url: window.location.href,
    };

    // Store in sessionStorage with a unique key (we'll clear sessionStorage later, but read this first)
    sessionStorage.setItem('__hyperke_logout_debug__', JSON.stringify(logoutInfo));

    console.log('[Auth Store] Logout TRIGGERED - persistent log written to sessionStorage: __hyperke_logout_debug__');

    // Clear store state
    token.value = null;
    user.value = null;
    personas.value = [];
    personaBaseToken.value = null;
    originalAdminToken.value = null;
    originalAdminUser.value = null;

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Persona switcher keys must not outlive the session (plan §9.10)
    localStorage.removeItem('personas');
    localStorage.removeItem('persona_base_token');
    // Pre-existing adjacent gap fixed in passing (plan §9.10): logout never
    // removed the impersonation stash, leaving `original_admin_token` behind.
    localStorage.removeItem('original_admin_token');
    localStorage.removeItem('original_admin_user');

    // Clear sessionStorage (comprehensive cleanup)
    // But keep our debug log for one more read
    const debugLog = sessionStorage.getItem('__hyperke_logout_debug__');
    sessionStorage.clear();
    if (debugLog) {
      sessionStorage.setItem('__hyperke_logout_debug__', debugLog);
    }
    
    // Clear cookies (if any exist)
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Clear cookie by setting it to expire in the past
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    });
    
    // Invalidate token on server (if JWT blacklist exists)
    // Note: This is optional - most JWT implementations are stateless
    // If you have a logout endpoint, call it here:
    // apiClient.post('/auth/logout').catch(() => {}); // Ignore errors
    
    console.log('[Auth Store] All storage cleared, redirecting to login');
    
    // Force hard redirect to ensure no cached state
    // Use window.location for absolute guarantee of navigation
    window.location.href = '/login';
    
    // Fallback: router push (in case window.location doesn't work)
    router.push({ name: 'Login', replace: true }).catch(() => {
      // If router fails, force navigation
      window.location.href = '/login';
    });
  }

  /**
   * Check auth status
   * Loads user from localStorage if token exists
   */
  function checkAuth(): void {
    console.log('[Auth Store] checkAuth() called - reading from localStorage');

    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    console.log('[Auth Store] checkAuth() localStorage state:', {
      hasToken: !!storedToken,
      tokenPrefix: storedToken ? storedToken.substring(0, 20) + '...' : 'none',
      hasUser: !!storedUser,
      userPreview: storedUser ? storedUser.substring(0, 100) + '...' : 'none',
    });

    if (storedToken && storedUser) {
      token.value = storedToken;
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('[Auth Store] checkAuth() parsed user:', {
          email: parsedUser?.email,
          role: parsedUser?.role,
          must_change_password: parsedUser?.must_change_password,
          parseSuccess: true,
        });

        // Ensure must_change_password field exists (for backward compatibility)
        if (parsedUser && typeof parsedUser.must_change_password === 'undefined') {
          parsedUser.must_change_password = false;
          console.log('[Auth Store] checkAuth() added missing must_change_password field');
        }
        user.value = parsedUser;

        console.log('[Auth Store] checkAuth() store updated:', {
          tokenSet: !!token.value,
          userSet: !!user.value,
          isAuthenticated: isAuthenticated.value,
        });

        // Boot refresh of the persona list (plan §7.1): when the cache is empty
        // but a session exists, re-derive it from GET /auth/personas. Guarded
        // once per page load because checkAuth() also runs from per-view
        // onMounted hooks (useAuth composable) — not just app boot.
        if (personas.value.length === 0 && !personasLoadAttempted) {
          personasLoadAttempted = true;
          void loadPersonas();
        }
      } catch (error) {
        console.error('[Auth Store] checkAuth() JSON parse error, logging out:', error);
        // Invalid stored user, clear auth
        logout();
      }
    } else {
      console.log('[Auth Store] checkAuth() no token/user in localStorage, store remains empty');
    }
  }

  /**
   * Load personas
   * Re-derives the linked-account list for the current token (GET /auth/personas)
   * and persists it to localStorage + the store. Non-fatal on error: the cached
   * list is kept (a stale entry simply 403s server-side if selected, plan §9.9).
   */
  async function loadPersonas(): Promise<void> {
    // Skip while impersonating: the persona list would be derived for the
    // impersonated user and clobber the admin's cached list (plan §9.6 —
    // never mix impersonation and persona switching).
    if (!token.value || isImpersonating.value) return;
    try {
      const response = await apiClient.get<{ personas: PersonaInfo[] }>('/auth/personas');
      personas.value = response.data.personas ?? [];
      localStorage.setItem('personas', JSON.stringify(personas.value));
    } catch (error) {
      console.warn('[Auth Store] loadPersonas() failed, keeping cached list:', error);
    }
  }

  /**
   * Switch persona
   * Peer identity swap within a persona group (POST /auth/switch-persona).
   * Clones the startImpersonation token-swap pattern: stash the original login
   * token once, swap token/user/personas in localStorage + store, then hard
   * reload so the whole app re-derives identity from the new token.
   *
   * @param targetUserId - id of the linked account to switch into
   */
  async function switchPersona(targetUserId: string): Promise<void> {
    if (isImpersonating.value) return; // never mix admin impersonation with persona switch (plan §9.6)
    console.log('[Auth Store] switchPersona() called:', { targetUserId });

    const response = await apiClient.post<LoginResponse>('/auth/switch-persona', {
      target_user_id: targetUserId,
    });

    // Stash the ORIGINAL login token once (A→B keeps A; B→C also keeps A)
    if (token.value && !localStorage.getItem('persona_base_token')) {
      personaBaseToken.value = token.value;
      localStorage.setItem('persona_base_token', token.value);
    }

    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    localStorage.setItem('personas', JSON.stringify(response.data.personas ?? []));
    token.value = response.data.token;
    user.value = response.data.user;
    personas.value = response.data.personas ?? [];

    console.log('[Auth Store] Persona switched:', {
      newUserId: response.data.user.id,
      newRole: response.data.user.role,
      personaCount: personas.value.length,
    });

    // must_change_password on the target routes to ChangePassword, same as login
    if (response.data.user.must_change_password === true) {
      window.location.href = '/change-password'; // route exists: requiresAuth
    } else {
      window.location.reload(); // same hard-reload contract as endImpersonation
    }
  }

  /**
   * Start impersonating a user
   * Stashes the original admin token AND user, then switches to the impersonation token
   *
   * @param impersonationToken - Impersonation JWT token
   * @param impersonatedUser - User data for impersonated user (includes impersonation_session_id)
   */
  async function startImpersonation(
    impersonationToken: string,
    impersonatedUser: User
  ): Promise<void> {
    console.log('[Auth Store] Starting impersonation:', {
      impersonatedUser: impersonatedUser.email,
      hasOriginalToken: !!token.value,
    });

    // Stash the ORIGINAL admin session (token AND user) BEFORE overwriting —
    // endImpersonation and the client.ts 401 rescue restore both.
    if (token.value) {
      originalAdminToken.value = token.value;
      localStorage.setItem('original_admin_token', token.value);
    }
    if (user.value) {
      originalAdminUser.value = user.value;
      localStorage.setItem('original_admin_user', JSON.stringify(user.value));
    }

    // Update with impersonation data
    localStorage.setItem('token', impersonationToken);
    localStorage.setItem('user', JSON.stringify(impersonatedUser));

    token.value = impersonationToken;
    user.value = impersonatedUser;

    console.log('[Auth Store] Impersonation started:', {
      isImpersonating: isImpersonating.value,
      adminUserId: impersonatedUser.admin_user_id,
    });
  }

  /**
   * End impersonation session
   * Ends the server-side session row (idempotent, best-effort) and restores
   * the original admin token AND user object.
   *
   * No arguments: all identifiers come from the store (the impersonated user
   * is the currently-loaded user; the admin session comes from the stash).
   */
  async function endImpersonation(): Promise<void> {
    const sessionId = user.value?.impersonation_session_id;
    const impersonatedUserId = user.value?.id;
    console.log('[Auth Store] Ending impersonation:', {
      impersonatedUserId,
      sessionId,
      hasOriginalToken: !!originalAdminToken.value,
      hasOriginalUser: !!originalAdminUser.value,
    });

    // Call API to end the session row. The EXPLICIT Authorization header
    // carries the stashed admin token — the request interceptor leaves
    // per-request Authorization headers alone (previously it overwrote this
    // with the impersonation token, so requireAdmin 403'd and the row stayed
    // open forever). Idempotent server-side; failure is non-fatal because the
    // local restore below is the source of truth.
    if (originalAdminToken.value) {
      try {
        await apiClient.post('/admin/end-impersonation', {
          session_id: sessionId,
          impersonated_user_id: impersonatedUserId,
          end_reason: 'logout',
        }, {
          headers: {
            Authorization: `Bearer ${originalAdminToken.value}`,
          },
          skipToast: true,
        });
        console.log('[Auth Store] Impersonation session ended on server');
      } catch (error) {
        console.warn('[Auth Store] Failed to end impersonation session on server (continuing with local restore):', error);
      }
    }

    // Restore the original admin session (token AND user object).
    if (originalAdminToken.value && originalAdminUser.value) {
      localStorage.setItem('token', originalAdminToken.value);
      localStorage.setItem('user', JSON.stringify(originalAdminUser.value));
      token.value = originalAdminToken.value;
      user.value = originalAdminUser.value;
      console.log('[Auth Store] Original admin token + user restored');
    } else {
      // Incomplete stash — we cannot restore the admin session, so clear
      // credentials and hard-redirect to login (no reload loop).
      console.warn('[Auth Store] No complete original admin stash — forcing re-login');
      token.value = null;
      user.value = null;
      originalAdminToken.value = null;
      originalAdminUser.value = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('original_admin_user');
      window.location.href = '/login';
      return;
    }

    // Clear the stash
    originalAdminToken.value = null;
    originalAdminUser.value = null;
    localStorage.removeItem('original_admin_token');
    localStorage.removeItem('original_admin_user');

    console.log('[Auth Store] Impersonation ended, reloading page to refresh user data');

    // Reload page to refresh user data with admin token
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  // Initialize auth on store creation
  checkAuth();

  return {
    user,
    token,
    loading,
    isAuthenticated,
    isImpersonating,
    originalAdminToken,
    originalAdminUser,
    personas,
    personaBaseToken,
    hasMultiplePersonas,
    login,
    logout,
    checkAuth,
    loadPersonas,
    switchPersona,
    startImpersonation,
    endImpersonation,
  };
});